/**
 * Plans — the single place prices and entitlements are defined. The pricing
 * page, the Settings billing panel, the Shop-tab gate and the new-card gate
 * all read from here. Stripe price ids live in server env vars
 * (STRIPE_PRICE_<PLAN>_<INTERVAL>) and are mapped in api/billing/checkout.ts.
 */

export type PlanId = 'free' | 'pro' | 'shop' | 'teams';
export type BillingInterval = 'monthly' | 'yearly';

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  /** Monthly price in cents. Yearly is priced as 10 months (two free). */
  monthlyCents: number;
  yearlyCents: number;
  /** Bullet list shown on the pricing page. */
  includes: string[];
  /** What this tier adds over the previous one — the pricing page headline. */
  headline: string;
  /** Purchasable through Stripe today. */
  purchasable: boolean;
  featured?: boolean;
}

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: 'One card. The whole design.',
    headline: 'Everything a card needs.',
    monthlyCents: 0,
    yearlyCents: 0,
    includes: [
      'One card, every frame and palette',
      'Public card page with your link and QR',
      'Save to phone (vCard) and Google Wallet pass',
      'Two-way Exchange contact',
      'Leads inbox',
      'All twenty social platforms',
    ],
    purchasable: false,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    tagline: 'Every identity you carry.',
    headline: 'Unlimited cards, full toolkit.',
    monthlyCents: 800,
    yearlyCents: 8000,
    includes: [
      'Unlimited cards in one swipeable stack',
      'Full analytics: views, taps, funnel, return visits',
      'Lead pipeline with tags, hot-lead detection, CSV',
      'AI bio, AI headshots, AI reply drafts',
      'Automations, events, tracking tags',
      'Webhooks to Zapier, Make, n8n, any CRM',
      'Starter kits to share your layout',
    ],
    purchasable: true,
    featured: true,
  },
  shop: {
    id: 'shop',
    name: 'Shop',
    tagline: 'Sell straight from your card.',
    headline: 'Pro, plus a shop with no cut.',
    monthlyCents: 1600,
    yearlyCents: 16000,
    includes: [
      'Everything in Pro',
      'Products with photos, badges, options and bullets',
      'Cart, checkout and a public order page',
      'Venmo, Cash App, PayPal, Zelle, Square links',
      'Card payments via your own Stripe account',
      'Orders inbox: confirm, fulfil, cancel, CSV',
      'Haphe takes 0% of every sale',
    ],
    purchasable: true,
  },
  teams: {
    id: 'teams',
    name: 'Teams',
    tagline: 'One brand, many cards.',
    headline: 'Shop, for a whole team.',
    monthlyCents: 3900,
    yearlyCents: 39000,
    includes: [
      'Everything in Shop',
      'Shared frames, logo and palette across every card',
      'One orders and leads inbox for the team',
      'Seats for up to five people',
    ],
    purchasable: false,
  },
};

export const PLAN_ORDER: PlanId[] = ['free', 'pro', 'shop', 'teams'];

export function planRank(id: PlanId | string | null | undefined): number {
  const i = PLAN_ORDER.indexOf((id ?? 'free') as PlanId);
  return i === -1 ? 0 : i;
}

/** Entitlements. Keep boolean and dumb — the DB triggers mirror these. */
export const FEATURES = {
  unlimitedCards: (p: PlanId) => planRank(p) >= planRank('pro'),
  shop: (p: PlanId) => planRank(p) >= planRank('shop'),
} as const;

export function priceFor(plan: Plan, interval: BillingInterval): number {
  return interval === 'yearly' ? plan.yearlyCents : plan.monthlyCents;
}

/** "$8" / "$6.67" per month equivalent for the yearly toggle. */
export function perMonthLabel(plan: Plan, interval: BillingInterval): string {
  const cents = interval === 'yearly' ? Math.round(plan.yearlyCents / 12) : plan.monthlyCents;
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

export function normalizePlan(raw: string | null | undefined): PlanId {
  return raw && (PLAN_ORDER as string[]).includes(raw) ? (raw as PlanId) : 'free';
}
